/* eslint-disable no-loop-func */
/* eslint-disable no-continue */
import * as rootTypes from '@/store/types';
import * as types from './types';
// eslint-disable-next-line import/no-cycle
import trackerApi from '../api/trackerApi';
import urlHelper from '@/core/mixins/urlHelper';
import trackerHelper from '@/core/mixins/trackerHelper';

function trackerHasErrors(trackerItem) {
  const checkForError = (tracker) => {
    if (!tracker) return;
    if (!tracker.hasOwnProperty('status') || !tracker.hasOwnProperty('isValid')) return;
    return tracker.status === 'COMPLETE' && !tracker.isValid;
  };
  const imageTrackerHasError = checkForError(trackerItem.impressionTracker);
  const clickTrackerError = checkForError(trackerItem.clickTracker);
  const imageTackersHasError = trackerItem.imageTrackers.some((item) => checkForError(item));
  const scriptTackersHasError = trackerItem.scriptTrackers.some((item) => checkForError(item));

  return imageTrackerHasError || clickTrackerError || imageTackersHasError || scriptTackersHasError;
}

function getTrackerCombination(creativeId, countries, regions, creatives, publisherCode) {
  const getCreativeName = (id) => {
    const creative = creatives.find((item) => (item.id === id));
    if (creative && creative.name) return creative.name;
    return '';
  };

  const getPublisherName = (code) => {
    if (code) return code;
    return 'ALL';
  };

  let locations = 'ALL';
  if (countries !== 'ALL' && regions === 'ALL') locations = countries;
  if (countries === 'ALL' && regions !== 'ALL') locations = regions;
  return `${getCreativeName(creativeId).split(' ').join('-')}_${getPublisherName(publisherCode).split(' ').join('-')}_${locations}`;
}

function getTrackersIDs(tracker) {
  const trackers = [];
  const row = { ...tracker };
  // eslint-disable-next-line guard-for-in
  for (const key in row) {
    const value = row[key];
    // Check if object is a Tracker or null
    // eslint-disable-next-line no-continue
    if (!key.includes('Tracker') || value == null) continue;
    if (!Array.isArray(value)) {
      trackers.push(value.trackerId);
      // eslint-disable-next-line no-continue
      continue;
    }
    const trackerIDs = value.flat().filter((item) => item != null).map((item) => item.trackerId);
    trackers.push(...trackerIDs);
  }
  return trackers;
}

function getManualTrackerToAddToItem(item, trackerType, trackerValue, scopeAndReference) {
  const { scope, referenceId } = scopeAndReference;

  let trackerValueToSend;

  if (typeof trackerValue === 'object') {
    trackerValueToSend = trackerValue.map((tracker) => tracker.replace(/"/g, '"'));
  } else {
    trackerValueToSend = [trackerValue.replace(/"/g, '"')];
  }

  return {
    creativeId: item.creativeId,
    publisherId: item.publisherId ? item.publisherId : null,
    countryIds: item.countryIds ? item.countryIds : [],
    geoGroupIds: item.geoGroupIds ? item.geoGroupIds : [],
    scope,
    referenceId,
    type: trackerType,
    trackers: trackerValueToSend,
  };
}

function getTrackerToSaveInStore(postManualTrackerResponse, trackerType) {
  return {
    creativeId: postManualTrackerResponse.creativeId,
    publisherId: postManualTrackerResponse.publisherId,
    countryIds: postManualTrackerResponse.countryIds,
    geoGroupIds: postManualTrackerResponse.geoGroupIds,
    uuid: postManualTrackerResponse.uuid,
    scope: postManualTrackerResponse.scope,
    contentItemUuid: postManualTrackerResponse.contentItemUuid,
    type: trackerType,
    impressionTracker: postManualTrackerResponse.type === 'IMPRESSION' ? postManualTrackerResponse.trackers[0] : null,
    clickTracker: postManualTrackerResponse.type === 'CLICK' ? postManualTrackerResponse.trackers[0] : null,
    postClickTracker: postManualTrackerResponse.type === 'POST_CLICK' ? postManualTrackerResponse.trackers[0] : null,
    scriptTrackers: postManualTrackerResponse.type === 'THIRD_PARTY_SCRIPT' ? postManualTrackerResponse.trackers : [],
    imageTrackers: postManualTrackerResponse.type === 'THIRD_PARTY_IMAGE' ? postManualTrackerResponse.trackers : [],
    landingPage: postManualTrackerResponse.trackers[0] ? postManualTrackerResponse.trackers[0].landingPage : null,
  };
}

const trackerConversionMap = {
  impressionTracker: 'IMPRESSION',
  clickTracker: 'CLICK',
  postClickTracker: 'POST_CLICK',
  scriptTrackers: 'THIRD_PARTY_SCRIPT',
  imageTrackers: 'THIRD_PARTY_IMAGE',
};

function convertTrackerType(type) {
  return trackerConversionMap[type];
}

const actions = {
  [types.FETCH_TRACKERS]: (context, campaignId) => trackerApi
    .getTrackers(campaignId)
    .then((campaignTrackers) => campaignTrackers),
  [types.FETCH_SELF_SERVICE_CONTENT_TRACKERS]: (context, payload) => trackerApi
    .getSelfServiceContentTrackers(
      context.rootState.campaigns.campaign.id,
      payload.creativeId,
    )
    .then((trackers) => {
      context.commit(types.SET_SELF_SERVICE_TRACKERS, trackers);
      return Promise.resolve(trackers);
    }),
  [types.FETCH_SELF_SERVICE_CONTENT_TRACKERS_MULTIPLE_CREATIVE]: (context, payload) => {
    trackerApi
      .getSelfServiceContentTrackers(
        context.rootState.campaigns.campaign.id,
        payload.creativeId,
      )
      .then((trackers) => {
        context.commit(types.SET_SELF_SERVICE_TRACKERS, trackers);
        return trackers;
      });
  },
  [types.RESET_IMPORT_TRACKERS]: ({ commit, dispatch }) => {
    dispatch(types.DELETE_TRACKER_VALIDATION_RESULTS);

    commit(types.SET_TRACKERS_FOR_VALIDATION, []);
    commit(types.SET_COLUMNS_TO_DISPLAY, []);
    commit(types.SET_PARSED_FILE_RESULT, { headers: [], rows: [] });
    commit(types.SET_TRACKERS_HEALTH_STATUS_DATA, []);
    commit(types.UPDATE_TRACKERS_SELECTED_COMBINATIONS, new Map());
  },
  [types.GET_TRACKERS_CSV]: async (content, campaignId) => trackerApi.getTrackersCSV(campaignId),
  [types.GET_TRACKERS_VALIDATION]: async ({ commit, state }, { campaignId }) => {
    const trackersHealthStatusData = await trackerApi.getTrackersValidations(campaignId);
    if (!trackersHealthStatusData) return;
    const filteredTrackersData = trackersHealthStatusData.filter((tracker) => state.parsedFileResult.some((parsedTracker) => tracker.uuid === parsedTracker.uuid));
    commit(types.SET_TRACKERS_HEALTH_STATUS_DATA, filteredTrackersData);
  },
  [types.PUT_TRACKERS_REVALIDATION]: async (context, campaignId) => trackerApi.putTrackersRevalidations(campaignId),
  [types.SET_IMPORTING_PLACEMENT_LEVEL_TRACKERS]: (context, payload) => {
    context.commit(types.SET_IMPORTING_PLACEMENT_LEVEL_TRACKERS, payload);
  },
  [types.PARSE_TRACKERS_FILE]: (context, payload) => {
    context.dispatch(types.RESET_IMPORT_TRACKERS);

    const fileName = payload.file.name;

    return trackerApi.parseTrackersFile(payload)
      .then((results) => {
        context.commit(types.SET_PARSED_FILE_RESULT, results);
        context.commit(types.SET_TRACKERS_FOR_VALIDATION, []);
      })
      .catch((error) => {
        context.commit(
          rootTypes.SET_GLOBAL_MESSAGE,
          {
            type: 'danger',
            message: `There was an error processing ${fileName}. Please try again later.`,
          },
          { root: true },
        );
        throw error;
      });
  },
  [types.SAVE_TRACKERS]: async ({ commit, rootState }, payload) => trackerApi.persistTrackers(rootState.campaigns.campaign.id, payload, rootState.trackers.fileUploadUuid).catch((error) => {
    const errorMessage = error.response.data?.status === 400 ? error.response.data?.message : 'There was an error persisting trackers. Please try again later.';
    commit(
      rootTypes.SET_GLOBAL_MESSAGE,
      {
        type: 'danger',
        message: errorMessage,
      },
      { root: true },
    );
    throw error;
  }),
  [types.SAVE_SELF_SERVICE_CONTENT_TRACKERS]: (context, payload) => {
    const { creativeId } = payload.body;
    if (!creativeId) {
      context.commit(rootTypes.SET_GLOBAL_MESSAGE, {
        message: 'Please set creative properties first',
        type: 'danger',
      }, { root: true });
      return false;
    }

    return trackerApi.saveSelfServiceContentTrackers(payload).then((response) => {
      const invalidTrackers = [response.impressionTracker, response.clickTracker, response.postClickTracker]
        .filter((tracker) => tracker && !!tracker.error);
      const allTrackersValid = invalidTrackers.length === 0;

      context.commit(rootTypes.SET_GLOBAL_MESSAGE, {
        type: allTrackersValid ? 'success' : 'danger',
        message: allTrackersValid ? 'Content trackers set' : invalidTrackers[0].error,
      }, { root: true });

      const trackers = {
        contentItemUuid: payload.body.contentItemUuid,
        impressionTracker: {
          valid: response.impressionTracker ? !response.impressionTracker?.error : null,
          value: response.impressionTracker?.value,
        },
        clickTracker: {
          valid: response.clickTracker ? !response.clickTracker?.error : null,
          value: response.clickTracker?.value,
        },
        postClickTracker: {
          valid: response.postClickTracker ? !response.postClickTracker?.error : null,
          value: response.postClickTracker?.value,
        },
      };
      context.commit(types.ADD_SELF_SERVICE_TRACKERS, trackers);
      return trackers;
    });
  },
  [types.CREATE_TRACKERS_FOR_VALIDATION]: async ({
    commit, dispatch, state, rootState,
  }, { referenceColumn }) => {
    const campaignId = rootState.campaigns.campaign.id;
    await dispatch(types.GET_TRACKERS_VALIDATION, { campaignId });
    const referenceColumnIndex = state.parsedFileHeaders.indexOf(referenceColumn);
    const hasCombination = (item) => {
      const hasCreativeIndex = item.creativeIndex != null;
      const hasPublisherIndex = item.publisherIndex != null;
      const hasGeoIndex = item.geoIndex != null;
      return hasCreativeIndex || hasPublisherIndex || hasGeoIndex;
    };
    const items = state.parsedFileResult.filter((item) => hasCombination(item))
      .map((item) => {
        let returnItem = {
          [referenceColumn]: item.row[referenceColumnIndex],
        };
        const trackerHealthInfo = state.trackersHealthStatusData.find((tracker) => tracker.uuid === item.uuid);
        const existingTrackerHealthInfo = state.selectedTrackersForValidation.find((tracker) => tracker.uuid === item.uuid);
        if (trackerHealthInfo) {
          const { imageTrackers, scriptTrackers } = trackerHealthInfo;
          const selected = true;
          const landingPageUrl = urlHelper.methods.removeUrlTrailingSlash(trackerHealthInfo?.landingPage);

          let matchingContentItemId = null;
          if (!state.isImportingPlacementLevelTrackers) {
            if (!existingTrackerHealthInfo || !existingTrackerHealthInfo?.contentItemId) matchingContentItemId = rootState.content.contentItems.find((cItem) => urlHelper.methods.removeUrlTrailingSlash(cItem.url) === landingPageUrl)?.id || null;
            else matchingContentItemId = existingTrackerHealthInfo?.contentItemId || null;
          }

          returnItem = {
            selected,
            ...returnItem,
            contentItemId: matchingContentItemId,
            creativeIndex: item?.creativeIndex,
            publisherIndex: item?.publisherIndex,
            geoIndex: item?.geoIndex,
            ...trackerHealthInfo,
            thirdPartyTags: trackerHelper.methods.assignThirdPartyTrackerType({ imageTrackers, scriptTrackers }),
          };
        }
        return returnItem;
      });
    commit(types.SET_TRACKERS_FOR_VALIDATION, items);
  },
  [types.UPDATE_TRACKERS_FOR_VALIDATION]: async ({
    commit, dispatch, state, rootState,
  }) => {
    const campaignId = rootState.campaigns.campaign.id;
    await dispatch(types.GET_TRACKERS_VALIDATION, { campaignId });
    const items = state.selectedTrackersForValidation.map((item) => {
      const trackerHealthInfo = state.trackersHealthStatusData.find((tracker) => tracker.uuid === item.uuid);
      const { imageTrackers, scriptTrackers } = trackerHealthInfo;
      const selected = item.selected ? item.selected : !trackerHasErrors(trackerHealthInfo);
      return {
        ...item,
        selected,
        ...trackerHealthInfo,
        thirdPartyTags: trackerHelper.methods.assignThirdPartyTrackerType({ imageTrackers, scriptTrackers }),
      };
    });
    commit(types.SET_TRACKERS_FOR_VALIDATION, items);
  },
  [types.UPDATE_TRACKERS_FOR_VALIDATION_REFERENCE_COLUMN]: ({ commit, state }, { items, previousReferenceColumn, newReferenceColumn }) => {
    if (!items || !previousReferenceColumn || !newReferenceColumn) return;
    const updatedItems = state.selectedTrackersForValidation.map((tracker, index) => {
      const updatedTracker = { ...tracker };
      const referenceColumnIndex = this.headers.indexOf(this.referenceColumn);
      delete updatedTracker[previousReferenceColumn];
      updatedTracker[newReferenceColumn] = items[index].row[referenceColumnIndex];
      return updatedTracker;
    });
    commit(types.SET_TRACKERS_FOR_VALIDATION, updatedItems);
  },
  [types.ADD_GROUPED_TRACKERS_LIST]: async ({
    commit, getters, rootState, dispatch,
  }, campaignId) => {
    const trackersResponse = await dispatch('FETCH_TRACKERS', campaignId);
    if (!trackersResponse) return {};
    const trackerGroupedList = {};
    trackersResponse.forEach((item) => {
      if (!item.countryIds || !item.geoGroupIds) return;

      const countries = getters.GET_COUNTRYS_BY_IDS(item.countryIds);
      const geos = getters.GET_GEOS_BY_IDS(item.geoGroupIds);
      const publisherCode = rootState.campaigns.publisherListMap.get(item.publisherId)?.code;

      const isAllAreas = item.countryIds.length === 0 && item.geoGroupIds.length === 0;
      const countryCode = isAllAreas ? null : countries;
      const geoCodes = isAllAreas ? null : geos;
      const trackerItem = {
        ...item,
        creativeId: item.creativeId,
        publisherCode,
        countryCode,
        geoCodes,
      };

      const { creatives } = rootState.content;
      const combination = getTrackerCombination(item.creativeId, countries, geos, creatives, publisherCode);
      trackerItem.group = combination;

      // update grouped list
      if (!trackerGroupedList[combination]) trackerGroupedList[combination] = [{ ...trackerItem }];
      else trackerGroupedList[combination].push({ ...trackerItem });
    });

    commit(types.SET_GROUPED_TRACKERS_LIST, trackerGroupedList);
    return trackerGroupedList;
  },
  [types.DELETE_GROUP_FROM_GROUPED_LIST]: async ({ commit, state, dispatch }, groupName) => {
    const group = state.groupedTrackersList[groupName];
    if (!group) return {};

    const trackersIDs = [];
    group.forEach((tracker) => {
      trackersIDs.push(getTrackersIDs(tracker));
    });
    const trackersResponse = await dispatch(types.DELETE_TRACKERS, trackersIDs);

    // Commit to the mutation on success. I need to pass the name of the group to be deleted
    if (trackersResponse === 200) {
      commit(types.REMOVE_GROUP_FROM_GROUPED_LIST, groupName);
    }
  },
  [types.ADD_TRACKER_TO_GROUPED_LIST_ITEM]: async ({ commit, rootState }, {
    groupName, item, trackerType, trackerValue, index = null,
  }) => {
    const scopeAndReference = {
      scope: item.scope === 'FEED' ? 'FEED' : 'CONTENT_ITEM',
      referenceId: item.scope === 'FEED' ? rootState.campaigns.campaign.feedId : item.encodedExternalId,
    };

    const trackerTypeToSend = convertTrackerType(trackerType);
    const trackerToAdd = getManualTrackerToAddToItem(item, trackerTypeToSend, trackerValue, scopeAndReference);

    const campaignId = rootState.campaigns.campaign.id;
    try {
      const tracker = await trackerApi.persistTrackersManually(campaignId, trackerToAdd);
      const convertedTracker = getTrackerToSaveInStore(tracker, trackerType);

      if (index == null) {
        commit(types.APPEND_ITEM_TO_GROUPED_LIST, {
          groupName,
          tracker: convertedTracker,
        });
      } else {
        commit(types.CHANGE_ITEM_IN_GROUPED_LIST, {
          groupName,
          index,
          trackerType,
          tracker: convertedTracker,
        });
      }
      return tracker;
    } catch (error) {
      commit(rootTypes.SET_GLOBAL_MESSAGE, {
        message: 'Couldn\'t add the new tracker!',
        type: 'danger',
      }, { root: true });
    }
  },
  [types.MANUALLY_ENTER_TRACKERS]: (context, payload) => trackerApi.persistTrackersManually(payload.campaignId, payload.trackerInfo)
    .then((response) => Promise.resolve(response)),
  [types.MANUALLY_UPDATE_TRACKER]: (context, payload) => trackerApi.updateATrackerValue(context.rootState.campaigns.campaign.id, payload.trackerId, { value: payload.trackerValue })
    .then((response) => Promise.resolve(response))
    .catch((error) => Promise.reject(error)),
  [types.UPDATE_ITEM_IN_GROUPED_LIST]: async ({ commit, rootState, state }, {
    groupName, index, trackerId, trackerType, trackerValue,
  }) => {
    try {
      const trackerNewValue = await trackerApi.updateATrackerValue(rootState.campaigns.campaign.id, trackerId, { value: trackerValue });
      const tracker = state.groupedTrackersList[groupName][index];
      tracker[trackerType] = trackerNewValue;
      // trackerNewValue is an object.
      commit(types.CHANGE_ITEM_IN_GROUPED_LIST, {
        groupName,
        index,
        trackerType,
        tracker,
      });
    } catch (error) {
      commit(rootTypes.SET_GLOBAL_MESSAGE, {
        message: 'Couldn\'t update the tracker!',
        type: 'danger',
      }, { root: true });
    }
  },
  [types.DELETE_ITEM_FROM_GROUPED_LIST]: async ({ commit, state, dispatch }, { groupName, index }) => {
    const group = state.groupedTrackersList[groupName];
    if (!group) return {};
    const item = group[index];
    if (!item) return {};

    const trackersIDs = getTrackersIDs(item);
    const trackersResponse = await dispatch(types.DELETE_TRACKERS, trackersIDs);

    // Commit to the mutation on success.
    if (trackersResponse === 200) {
      commit(types.REMOVE_ITEM_FROM_GROUPED_LIST, {
        groupName,
        index,
      });
    }
  },
  [types.DELETE_TRACKER_FROM_GROUPED_LIST_ITEM]: async ({ commit, state, dispatch }, {
    groupName, index, trackerId, trackerType, trackerValue,
  }) => {
    const trackersResponse = await dispatch(types.DELETE_TRACKERS, [trackerId]);
    const trackerInState = state.groupedTrackersList[groupName][index];
    trackerInState[trackerType] = trackerValue;
    // Commit to the mutation on success.
    if (trackersResponse === 200) {
      commit(types.CHANGE_ITEM_IN_GROUPED_LIST, {
        groupName,
        index,
        trackerType,
        tracker: trackerInState,
      });
    }
  },
  [types.DELETE_TRACKERS]: async (context, trackerIds) => trackerApi.deleteTrackers(context.rootState.campaigns.campaign.id, trackerIds)
    .then((response) => response.status)
    .catch((error) => {
      throw error;
    }),
  [types.DELETE_TRACKER_VALIDATION_RESULTS]: async ({ state, rootState }) => {
    const { fileUploadUuid } = state;
    if (fileUploadUuid) {
      trackerApi.deleteTrackersValidations(rootState.campaigns.campaign.id, fileUploadUuid);
    }
  },
};
export default actions;
